import { FormikProps } from 'formik';
import * as React from 'react';
import * as Yup from 'yup';
import { Element } from 'react-scroll';
import {
  SubForm,
  FormSection,
  Heading,
  Box,
  ISubFormData,
  IKeysAsAny,
} from '@alinta-digital/asg';
import { ErrorNotification } from '../../components/ErrorNotification';
import { PlanTile } from '../../components/PlanTile/';
import { FuelSite, ActionableFuelSite } from '../../types';
import { FormContainerContext } from '../../contexts/FormContainerContext';
import {
  setFormContextError,
  scrollToScrollElement,
  resetFormContextError,
  deepCopy,
  capitalizeFirstLetter,
  pushAnalytics,
} from '../../utils/utils';
import { FuelType } from '../../types';
import { DirectDebitForm } from '../../views/Form/Form';
import { AccountType } from '../../types';
import { ButtonV2, StyledText } from '../../Common.Styled';

interface ConnectionSubForm {
  electricity: FuelSite[];
  gas: FuelSite[];
}

export interface ConnectionParentForm {
  connection: ConnectionSubForm;
}

interface ConnectionSubFormProps {
  formikProps: FormikProps<DirectDebitForm>;
  isEdit: boolean;
  onPreviousClick: () => void;
  onNextClick: () => void;
}

export const ConnectionSubForm: React.FC<ConnectionSubFormProps> = ({
  formikProps,
  onPreviousClick,
  onNextClick,
}) => {
  const {
    values: formContextValues,
    dispatch: formContextDispatch,
  } = React.useContext(FormContainerContext);
  const { error: formContextError } = formContextValues;
  const {
    electricity: parentFormElectricity,
    gas: parentFormGas,
  } = formikProps.values.connection;

  function selectSites(
    subFormFormikProps: FormikProps<
      ISubFormData<ConnectionParentForm, IKeysAsAny<ConnectionSubForm>>
    >,
    authAccountNumber: string,
    accountNumber: string,
    fuelType: FuelType,
    accountType: AccountType
  ) {
    const newSubFormValues = deepCopy(subFormFormikProps.values);
    let found = false;
    const newSites = newSubFormValues.connection[fuelType].filter(
      (selected: FuelSite) => {
        if (selected.accountNumber === accountNumber) {
          found = true;
          return false;
        }
        return true;
      }
    );

    if (!found) {
      newSites.push({
        authAccountNumber,
        accountNumber,
        fuelType,
        accountType,
      });
    }
    newSubFormValues.connection[fuelType] = newSites;

    subFormFormikProps.setValues(newSubFormValues);
  }

  const fuelAccountCount = formContextValues.customer.fuelSites.length;

  React.useEffect(() => {
    pushAnalytics('load.0.dd.s2');
  }, []);

  React.useEffect(() => {
    if (
      !formContextError.steps[1].status &&
      (fuelAccountCount > 4 || fuelAccountCount === 0)
    ) {
      setFormContextError(
        formContextValues,
        formContextDispatch,
        1,
        true,
        'Service Limit'
      );
    }

    if (formContextError.steps[1].status) {
      scrollToScrollElement('notificationError');
    } else {
      scrollToScrollElement('connectionStart');
    }
  }, [
    formContextError.steps,
    fuelAccountCount,
    formContextValues,
    formContextDispatch,
  ]);

  return (
    <SubForm<ConnectionParentForm, ConnectionSubForm>
      formikProps={formikProps}
      config={{
        connection: {
          electricity: {
            validation: Yup.string(),
            initialValue: parentFormElectricity || [],
          },
          gas: {
            validation: Yup.string(),
            initialValue: parentFormGas || [],
          },
        },
      }}
      valuesKey="connection"
    >
      {({ subFormFormikProps }) => {
        const {
          electricity: subFormElectricity,
          gas: subFormGas,
        } = subFormFormikProps.values.connection;

        const selectedSitesAccountNumbers: string[] = [
          ...subFormElectricity,
          ...subFormGas,
        ].map((site: FuelSite) => {
          return site.accountNumber;
        });

        const fuelSites = formContextValues.customer.fuelSites.map(
          (site: FuelSite) => {
            const newSite: ActionableFuelSite = {
              ...site,
              checked:
                selectedSitesAccountNumbers.indexOf(site.accountNumber) !== -1,
              onClick: () => {
                pushAnalytics('click.1.dd.s2.acb');
                selectSites(
                  subFormFormikProps,
                  site.authAccountNumber,
                  site.accountNumber,
                  site.fuelType,
                  site.accountType
                );
              },

              label: `${site.fuelType} account: ${site.authAccountNumber}`,
            };

            return newSite;
          }
        );

        const fuelType = fuelSites[0].fuelType;

        return (
          <FormSection
            id="connection"
            heading="Step 2: Your connection"
            stepNumber="2"
            totalSteps="4"
            previousButton={
              !formContextError.steps[1].status ||
              (formContextError.steps[1].status &&
                formContextError.steps[1].type === 'Credit card limit') ? (
                <ButtonV2
                  id="connectionPreviousButton"
                  data-testid="connection_previousButton"
                  secondary={true}
                  type="button"
                  onClick={() => {
                    resetFormContextError(
                      formContextValues,
                      formContextDispatch,
                      1
                    );
                    pushAnalytics('click.2.dd.s2.back');
                    onPreviousClick();
                  }}
                >
                  Back
                </ButtonV2>
              ) : (
                undefined
              )
            }
            nextButton={
              !formContextError.steps[1].status ||
              (formContextError.steps[1].status &&
                formContextError.steps[1].type === 'Credit card limit') ? (
                <ButtonV2
                  id="connectionNextButton"
                  data-testid="connection_nextButton"
                  primary={true}
                  type="button"
                  disabled={
                    subFormElectricity.length === 0 && subFormGas.length === 0
                  }
                  onClick={async () => {
                    if (
                      formContextError.steps[1].status &&
                      formContextError.steps[1].type === 'Credit card limit'
                    ) {
                      setFormContextError(
                        formContextValues,
                        formContextDispatch,
                        1,
                        false,
                        undefined
                      );
                    }

                    formikProps.setValues({
                      ...formikProps.values,
                      ...subFormFormikProps.values,
                    });
                    pushAnalytics('click.3.dd.s2.next');
                    onNextClick();
                  }}
                >
                  Next
                </ButtonV2>
              ) : (
                undefined
              )
            }
          >
            {formContextError.steps[1].status &&
            formContextError.steps[1].type !== 'Credit card limit' ? (
              <ErrorNotification
                errorType={formContextError.steps[1].type}
                stateShortName={formContextValues.stateShortName}
              />
            ) : (
              <>
                <Element name="connectionStart" id="connectionStart" />
                <Box margin={{ bottom: 'xsmall' }}>
                  <Heading level={3} color="#000F20" weight={700}>
                    {capitalizeFirstLetter(fuelType)}
                  </Heading>
                </Box>
                <Box margin={{ bottom: 'small' }}>
                  <StyledText lineHeight="21px">
                    Select the tick box below to confirm your{' '}
                    {capitalizeFirstLetter(fuelType)} reference number and to
                    continue setting up Direct Debit.
                  </StyledText>
                </Box>
                {formContextError.steps[1].status &&
                  formContextError.steps[1].type === 'Credit card limit' && (
                    <ErrorNotification
                      margin={{ bottom: 'small' }}
                      errorType={formContextError.steps[1].type}
                      stateShortName={formContextValues.stateShortName}
                    />
                  )}
                <PlanTile fuelSites={fuelSites} />
              </>
            )}
          </FormSection>
        );
      }}
    </SubForm>
  );
};
